/**
 * Serviço: Central de atendimento do WhatsApp (#252)
 *
 * Chamadas às rotas /whatsapp/inbox (Atendente, Gerente, Proprietário e super admin).
 *
 * @author Equipe VendeAi
 * @since 2.6.0
 */

import { api } from './api';
import type { OrderStatus, OrderType } from '@/types';

/** Robô atendendo, atendimento humano (robô pausado) ou robô desligado na loja */
export type BotState = 'BOT' | 'HUMAN' | 'OFF';
export type PauseReason = 'STORE_PHONE' | 'AGENT_TAKEOVER' | 'CUSTOMER_REQUEST';
export type ConversationFilter = 'all' | 'unread' | 'human' | 'openOrder';
export type MessageOrigin = 'CUSTOMER' | 'SYSTEM' | 'PANEL' | 'PHONE';
export type InboxMessageType =
  | 'TEXT'
  | 'IMAGE'
  | 'VIDEO'
  | 'AUDIO'
  | 'DOCUMENT'
  | 'STICKER'
  | 'LOCATION'
  | 'CONTACT'
  | 'ORDER';

export interface Conversation {
  id: string;
  jid: string;
  name: string | null;
  phone: string | null;
  avatarUrl: string | null;
  customerId: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageFromMe: boolean;
  unreadCount: number;
  botState: BotState;
  pauseReason: PauseReason | null;
  pausedAt: string | null;
  pausedByName: string | null;
  openOrders: number;
}

export interface ConversationCustomer {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  notes: string | null;
  avatarUrl: string | null;
  createdAt: string;
  addresses: Array<{
    id: string;
    label: string | null;
    street: string;
    number: string;
    complement: string | null;
    neighborhood: string;
    city: string;
    state: string;
    zipCode: string;
    isDefault: boolean;
  }>;
  recentOrders: Array<{
    id: string;
    orderNumber: number;
    status: OrderStatus;
    type: OrderType;
    channel: string;
    total: number;
    createdAt: string;
  }>;
  ordersCount: number;
  openOrders: number;
}

export interface ConversationDetail extends Omit<
  Conversation,
  'lastMessagePreview' | 'lastMessageFromMe' | 'openOrders'
> {
  pushName: string | null;
  /** Texto do celular que pausou o robô (STORE_PHONE): permite "Foi mensagem automática" */
  pausedMessageText: string | null;
  whatsappConnected: boolean;
  customer: ConversationCustomer | null;
}

/** Situação do arquivo de uma mídia recebida (#253) */
export type MediaStatus =
  | 'PENDING'
  | 'READY'
  | 'FAILED'
  | 'TOO_LARGE'
  | 'VIEW_ONCE'
  | 'UNSUPPORTED'
  | 'DELETED'
  /** Contato passou da cota de mídias do dia (#275) */
  | 'DAILY_LIMIT'
  /** Espaço das mídias da loja cheio (#275) */
  | 'STORAGE_FULL';

export interface InboxMessage {
  id: string;
  messageId: string;
  fromMe: boolean;
  type: InboxMessageType;
  content: string | null;
  caption: string | null;
  /** Link de exibição (mídia recebida: assinado, vale poucos minutos) */
  mediaUrl: string | null;
  /** Endereço estável da mídia privada (renova o link assinado), #253 */
  mediaRef?: string | null;
  mediaMimetype: string | null;
  /** Situação da mídia recebida (#253) */
  mediaStatus?: MediaStatus | null;
  mediaSize?: number | null;
  /** Ainda dá para baixar de novo ("Tentar de novo"): dentro do prazo de guarda das mídias */
  mediaRetryable?: boolean;
  /** Acima do limite de mídias do dia do contato hoje, no fuso da loja ("Tentar de novo" só amanhã) */
  mediaDailyLimitToday?: boolean;
  /** Só no painel: quando o atendente pediu "Tentar de novo" (conta o tempo de "Baixando...") */
  retriedAt?: number;
  location: {
    latitude: number;
    longitude: number;
    name?: string | null;
    address?: string | null;
  } | null;
  timestamp: string;
  origin: MessageOrigin;
  sentByName: string | null;
}

export interface QuickReply {
  id: string;
  shortcut: string;
  content: string;
}

export type SendResult =
  | { status: 'sent'; messageId: string }
  | { status: 'queued'; requestId: string };

export async function listConversations(params: {
  search?: string;
  filter?: ConversationFilter;
  page?: number;
  perPage?: number;
}) {
  const { data } = await api.get<{ data: Conversation[]; total: number; totalPages: number }>(
    '/whatsapp/inbox/conversations',
    { params },
  );
  return data;
}

export async function getUnreadSummary(): Promise<{ conversations: number; messages: number }> {
  const { data } = await api.get('/whatsapp/inbox/unread');
  return data.data;
}

export async function getConversation(id: string): Promise<ConversationDetail> {
  const { data } = await api.get(`/whatsapp/inbox/conversations/${id}`);
  return data.data;
}

/** Cursor do histórico: horário e id da mensagem mais antiga já carregada */
export interface MessagesCursor {
  before: string;
  beforeId: string | null;
}

export async function listMessages(id: string, cursor?: MessagesCursor | null, limit = 30) {
  const { data } = await api.get<{
    data: InboxMessage[];
    hasMore: boolean;
    nextBefore: string | null;
    nextBeforeId: string | null;
  }>(`/whatsapp/inbox/conversations/${id}/messages`, {
    params: { before: cursor?.before, beforeId: cursor?.beforeId ?? undefined, limit },
  });
  return data;
}

export async function markRead(id: string): Promise<void> {
  await api.post(`/whatsapp/inbox/conversations/${id}/read`);
}

export async function takeOver(id: string): Promise<ConversationDetail> {
  const { data } = await api.post(`/whatsapp/inbox/conversations/${id}/takeover`);
  return data.data;
}

export async function releaseToBot(id: string): Promise<ConversationDetail> {
  const { data } = await api.post(`/whatsapp/inbox/conversations/${id}/release`);
  return data.data;
}

/**
 * "Foi mensagem automática do WhatsApp Business": devolve ao robô e o texto entra na
 * lista da loja de textos automáticos (não pausa mais o robô)
 */
export async function markAutoMessage(id: string): Promise<ConversationDetail> {
  const { data } = await api.post(`/whatsapp/inbox/conversations/${id}/auto-message`);
  return data.data;
}

export interface IgnoredAutoText {
  id: string;
  text: string;
  /** MANUAL: a equipe marcou na Central; LEARNED: reconhecido sozinho */
  source: 'MANUAL' | 'LEARNED';
  createdAt: string;
}

export async function listIgnoredAutoTexts(): Promise<IgnoredAutoText[]> {
  const { data } = await api.get('/whatsapp/inbox/ignored-auto-texts');
  return data.data;
}

export async function deleteIgnoredAutoText(id: string): Promise<void> {
  await api.delete(`/whatsapp/inbox/ignored-auto-texts/${id}`);
}

export async function sendText(id: string, text: string): Promise<SendResult> {
  const { data } = await api.post(`/whatsapp/inbox/conversations/${id}/messages`, { text });
  return data.data;
}

export async function sendFile(id: string, file: File, caption?: string): Promise<SendResult> {
  const form = new FormData();
  form.append('file', file);
  if (caption) form.append('caption', caption);
  const { data } = await api.post(`/whatsapp/inbox/conversations/${id}/messages`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.data;
}

export async function listQuickReplies(): Promise<QuickReply[]> {
  const { data } = await api.get('/whatsapp/inbox/quick-replies');
  return data.data;
}

export async function saveQuickReply(
  input: { shortcut: string; content: string },
  id?: string,
): Promise<QuickReply> {
  const { data } = id
    ? await api.put(`/whatsapp/inbox/quick-replies/${id}`, input)
    : await api.post('/whatsapp/inbox/quick-replies', input);
  return data.data;
}

export async function deleteQuickReply(id: string): Promise<void> {
  await api.delete(`/whatsapp/inbox/quick-replies/${id}`);
}

/** "Tentar de novo" na mídia recebida que não foi baixada (#253) */
/** Pede o download de novo; devolve a situação atual da mídia */
export async function retryMessageMedia(
  contactId: string,
  messageRowId: string,
): Promise<{ mediaStatus: MediaStatus | null }> {
  const res = await api.post(
    `/whatsapp/inbox/conversations/${contactId}/messages/${messageRowId}/media/retry`,
  );
  return { mediaStatus: (res.data?.data?.mediaStatus ?? null) as MediaStatus | null };
}
